import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Box, Grid, Divider, Card, CardContent,
} from '@mui/material';
import { CheckCircle, ArrowForward } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import { useDuration } from 'context/DurationContext';
import { useCountry } from 'context/CountryContext';
import {
  readTierConfig, tierPriceCells, readAddOnIds, orderByFlow, questionSteps,
} from './mappingUtils';

const TIER_COLOR = { low: '#34d399', mid: '#fbbf24', high: '#a78bfa' };
const TIER_LABEL = { low: 'Low', mid: 'Mid', high: 'High' };
const TIERS = ['low', 'mid', 'high'];

const formatDurationLabel = (duration) => {
  if (!duration) return '';
  if (duration.displayLabel) return duration.displayLabel;
  if (duration.label) return duration.label;
  const value = duration.value || 0;
  const unit = duration.unit || 'month';
  const unitLabels = { day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter', biannual: '6 Months', year: 'Year', custom: 'Custom' };
  return `${value} ${unitLabels[unit] || unit}${value > 1 ? 's' : ''}`;
};

function ViewMappingModal({ open, onClose, mapping, questions, features = [], addOns = [] }) {
  const { durations } = useDuration();
  const { countries } = useCountry();

  if (!mapping) return null;

  const { featuresByTier } = readTierConfig(mapping);
  const selectedAddOns = readAddOnIds(mapping)
    .map(id => addOns.find(a => a.id === id) || { id, name: `Add-on ${String(id).slice(-6)}` });

  const featureName = (id) => features.find(f => f.id === id)?.name || `Feature ${String(id).slice(-6)}`;
  const durationName = (id) => formatDurationLabel(durations.find(d => d.id === id)) || `Duration ${String(id).slice(-6)}`;
  const currencySym = (countryId) => {
    const c = countries.find(x => x.id === countryId);
    return c?.currencySymbol || c?.currencyCode || '';
  };
  const countryName = (countryId) => {
    const c = countries.find(x => x.id === countryId);
    return c?.countryName || c?.name || '';
  };

  const scopedQuestions = questions.filter((q) => q.questionnaireId === mapping.questionnaireId);
  const steps = questionSteps(scopedQuestions);

  // Conditions in the order the user meets them, labelled with the step
  // number they see on the questionnaire ("Q7" = 7th question shown).
  const buildQuestionAnswerPairs = () => {
    const pairs = [];
    const answerData = mapping.answerCombination || {};
    for (const questionId of orderByFlow(scopedQuestions, Object.keys(answerData))) {
      const answerIds = answerData[questionId];
      const question = questions.find((q) => q.id === questionId);
      if (!question) continue;
      const ids = Array.isArray(answerIds) ? answerIds : [answerIds];
      const answerTexts = ids.map((answerId) => {
        const option = question.answerOptions?.find((opt) => opt.id === answerId);
        return option ? option.answerText : `Unknown (${String(answerId).slice(-8)})`;
      });
      pairs.push({ questionId: question.id, step: steps[question.id], questionText: question.text, questionType: question.type, answerTexts });
    }
    return pairs;
  };

  const questionAnswerPairs = buildQuestionAnswerPairs();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <span>Mapping Details</span>
          <MDBadge
            badgeContent={mapping.active ? 'Active' : 'Inactive'}
            color={mapping.active ? 'success' : 'secondary'}
            variant="gradient" size="sm"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <MDBox pt={2}>
          {/* Visual Flow */}
          <Box mb={4}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Card variant="outlined" sx={{ bgcolor: 'primary.lighter', borderColor: 'primary.main' }}>
                  <CardContent>
                    <MDTypography variant="h6" color="primary" mb={2}>IF User Selects:</MDTypography>
                    <Box>
                      {questionAnswerPairs.length === 0 ? (
                        <MDTypography variant="caption" color="text.secondary">No answers configured</MDTypography>
                      ) : (
                        questionAnswerPairs.map((pair, index) => (
                          <Box key={pair.questionId} mb={2}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                              <CheckCircle color="primary" fontSize="small" sx={{ mt: 0.5 }} />
                              <Box flex={1}>
                                <MDTypography variant="caption" color="text.secondary" display="block">
                                  Q{pair.step || index + 1}: {pair.questionText}
                                  {pair.questionType === 'multiple' && <Chip label="Multiple" size="small" sx={{ ml: 1 }} />}
                                </MDTypography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                  {pair.answerTexts.map((text, idx) => (
                                    <Chip key={idx} label={text} size="small" color="primary" variant="outlined" />
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2}>
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <ArrowForward sx={{ fontSize: 48, color: 'info.main' }} />
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card variant="outlined" sx={{ bgcolor: 'success.lighter', borderColor: 'success.main' }}>
                  <CardContent>
                    <MDTypography variant="h6" color="success" mb={2}>THEN Offer These Tiers:</MDTypography>
                    <Grid container spacing={1.5}>
                      {TIERS.map(tier => {
                        const tc = TIER_COLOR[tier];
                        const featIds = featuresByTier[tier] || [];
                        const priceCells = tierPriceCells(mapping, tier);
                        return (
                          <Grid item xs={12} key={tier}>
                            <Box p={1.5} border="2px solid" borderColor={`${tc}`} borderRadius={1} bgcolor="white">
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Chip label={TIER_LABEL[tier]} size="small" sx={{ bgcolor: `${tc}22`, color: tc, fontWeight: 700 }} />
                                <MDTypography variant="caption" color="text.secondary">{featIds.length} feature{featIds.length !== 1 ? 's' : ''}</MDTypography>
                              </Box>
                              {priceCells.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                  {priceCells.map((cell, i) => (
                                    <Chip key={i} size="small" variant="outlined"
                                      label={`${durationName(cell.durationId)}: ${currencySym(cell.countryId)}${cell.finalPrice}`} />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Per-tier breakdown */}
          <MDTypography variant="h6" mb={2}>Tier Breakdown</MDTypography>
          <Grid container spacing={2}>
            {TIERS.map(tier => {
              const tc = TIER_COLOR[tier];
              const featIds = featuresByTier[tier] || [];
              const priceCells = tierPriceCells(mapping, tier);
              return (
                <Grid item xs={12} md={4} key={tier}>
                  <Box border="2px solid" borderColor={`${tc}`} borderRadius={1} p={2} height="100%">
                    <Chip label={TIER_LABEL[tier]} size="small" sx={{ bgcolor: `${tc}22`, color: tc, fontWeight: 700, mb: 1.5 }} />

                    <MDTypography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
                      Pricing
                    </MDTypography>
                    {priceCells.length === 0 ? (
                      <MDTypography variant="caption" color="text.secondary" display="block" mb={1.5}>No pricing configured</MDTypography>
                    ) : (
                      <Box mb={1.5}>
                        {priceCells.map((cell, i) => {
                          const sym = currencySym(cell.countryId);
                          const hasStrike = cell.priceBeforeDiscount && Number(cell.priceBeforeDiscount) > Number(cell.finalPrice);
                          return (
                            <Box key={i} display="flex" justifyContent="space-between" alignItems="baseline" py={0.3}>
                              <MDTypography variant="caption" color="text.secondary">
                                {durationName(cell.durationId)}{countryName(cell.countryId) ? ` · ${countryName(cell.countryId)}` : ''}
                              </MDTypography>
                              <Box display="flex" alignItems="baseline" gap={0.5}>
                                {hasStrike && (
                                  <MDTypography variant="caption" sx={{ textDecoration: 'line-through', color: '#9ca3af' }}>
                                    {sym}{cell.priceBeforeDiscount}
                                  </MDTypography>
                                )}
                                <MDTypography variant="button" fontWeight="bold">{sym}{cell.finalPrice}</MDTypography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    <MDTypography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
                      Features ({featIds.length})
                    </MDTypography>
                    {featIds.length === 0 ? (
                      <MDTypography variant="caption" color="text.secondary">No features assigned</MDTypography>
                    ) : (
                      <Box>
                        {featIds.map(id => (
                          <Box key={id} display="flex" alignItems="center" gap={0.5} py={0.2}>
                            <CheckCircle sx={{ fontSize: 14, color: tc }} />
                            <MDTypography variant="caption">{featureName(id)}</MDTypography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Add-ons */}
          <Divider sx={{ my: 3 }} />
          <MDTypography variant="h6" mb={2}>Add-ons ({selectedAddOns.length})</MDTypography>
          {selectedAddOns.length === 0 ? (
            <MDTypography variant="caption" color="text.secondary">No add-ons attached</MDTypography>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {selectedAddOns.map(addOn => (
                <Chip
                  key={addOn.id}
                  label={addOn.baseMonthlyPrice !== undefined && addOn.baseMonthlyPrice !== null && addOn.baseMonthlyPrice !== ''
                    ? `${addOn.name} · $${addOn.baseMonthlyPrice}/mo`
                    : addOn.name}
                  size="small" color="info" variant="outlined"
                />
              ))}
            </Box>
          )}

          {(mapping.createdAt || mapping.updatedAt) && (
            <>
              <Divider sx={{ my: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text.secondary" display="block">Created At</MDTypography>
                  <MDTypography variant="button">{mapping.createdAt ? new Date(mapping.createdAt).toLocaleString() : '—'}</MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text.secondary" display="block">Updated At</MDTypography>
                  <MDTypography variant="button">{mapping.updatedAt ? new Date(mapping.updatedAt).toLocaleString() : '—'}</MDTypography>
                </Grid>
              </Grid>
            </>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="info">Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default ViewMappingModal;
